import { CommonModule } from '@angular/common';
import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
  SelectChangeEventDetail,
} from '@ionic/angular/standalone';
import { IonSelectCustomEvent } from '@ionic/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FreightAvailableStoreService } from '../freight-available-store.service';
import { CountryStateService } from '@okcargo-apps/shared-infrastructure-ionic';

@Component({
  selector: 'lib-freight-available-search-modal',
  templateUrl: './freight-available-search-modal.component.html',
  styleUrls: ['./freight-available-search-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonSelect,
    IonSelectOption,
    IonRow,
    IonCol,
    IonGrid,
    IonFooter,
    IonContent,
    IonIcon,
    IonButton,
    IonButtons,
    IonTitle,
    IonToolbar,
    IonHeader,
    TranslateModule,
  ],
})
export class FreightAvailableSearchModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly countryStateService = inject(CountryStateService);
  private readonly freightAvailableStoreService = inject(FreightAvailableStoreService);

  public readonly countries = this.countryStateService.countries;

  private readonly fromCountry = signal<string | undefined>('ES');
  private readonly toCountry = signal<string | undefined>('ES');

  public readonly fromStates = this.countryStateService.statesForCountryCode(this.fromCountry);
  public readonly toStates = this.countryStateService.statesForCountryCode(this.toCountry);

  public searchForm: FormGroup;

  constructor() {
    this.searchForm = this.fb.group({
      fromCountry: ['ES'],
      fromState: [''],
      toCountry: ['ES'],
      toState: [''],
    });
  }

  public async confirm(): Promise<boolean> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    loading.present();

    this.freightAvailableStoreService.applyFilters({
      originCountryCode: this.searchForm.get('fromCountry')?.value,
      originStateCode: this.searchForm.get('fromState')?.value,
      destinationCountryCode: this.searchForm.get('toCountry')?.value,
      destinationStateCode: this.searchForm.get('toState')?.value,
    });

    await loading.dismiss();
    return this.modalCtrl.dismiss(null, 'confirm');
  }

  public cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  public handleChangeFromCountry(event: IonSelectCustomEvent<SelectChangeEventDetail<any>>): void {
    this.fromCountry.set(event.detail.value);
    this.searchForm.get('fromState')?.reset();
  }

  public handleChangeToCountry(event: IonSelectCustomEvent<SelectChangeEventDetail<any>>): void {
    this.toCountry.set(event.detail.value);
    this.searchForm.get('toState')?.reset();
  }
}
